import { Button } from "@/components/ui/button";
import { ArrowRight, Mountain, Store, CheckCircle2, Clock } from "lucide-react";
import Overlander2InterestDialog from "@/components/site/Overlander2InterestDialog";

const MODELS = [
  {
    id: "overlander-4",
    name: "Overlander 4",
    tagline: "Built for the wild.",
    audience: "For adventurers, farmers & the outback ready",
    status: "Available now",
    statusColor: "bg-green-600/15 text-green-700 border-green-600/40",
    price: "From $39,990",
    icon: Mountain,
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_aussie-4wd-hub/artifacts/l07jforl_Joshua%20Cady%203.png",
    imageCredit: "Photo · Joshua Cady · WA",
    highlights: [
      "Genuine 4-wheel drive · 4 × 750 W hub motors",
      "35° gradient · 250 mm clearance · IP-sealed",
      "Up to 72 km range (Standard)",
      "Class 3 legal — footpath, road, bush, beach",
    ],
    cta: { label: "Book my trial", href: "#test-drive", primary: true },
    booked: true,
  },
  {
    id: "overlander-2",
    name: "Overlander 2",
    tagline: "Built for the everyday.",
    audience: "For town, tracks, cafes & the daily loop",
    status: "Coming soon",
    statusColor: "bg-amber-500/15 text-amber-700 border-amber-500/40",
    price: "From $25,000",
    priceNote: "Introductory offer",
    icon: Store,
    image:
      "https://customer-assets.emergentagent.com/job_aussie-4wd-hub/artifacts/m8pd42s9_Flatlander%20Full%20frame.png",
    highlights: [
      "Nimble 2-wheel drive · lighter & easier to transport",
      "Perfect for footpaths, parks, esplanades, shops",
      "Same Class 3 dual-purpose legality",
      "Same great engineering, everyday-friendly footprint",
    ],
    cta: { label: "Register interest", href: "#test-drive", primary: false },
    booked: false,
  },
];

// Single source of truth for the side-by-side compare table.
// `delta` shows a small chip on the winning side of a numeric trade-off.
const COMPARE_ROWS = [
  { k: "Drivetrain", o4: "Four-wheel drive, 4 × independent hub motors", o2: "Two-wheel drive, 2 × hub motors" },
  { k: "Motor power", o4: "4 × 750 W peak (3,000 W combined)", o2: "2 × 750 W", delta: { side: "o4", label: "+1,500 W" } },
  { k: "Battery (standard)", o4: "2 × 12 V / 100 Ah lithium (24 V system)", o2: "2 × 12 V / 100 Ah lithium (24 V system)" },
  { k: "Battery (upgrades)", o4: "24 V / 150 Ah or 24 V / 200 Ah lithium", o2: "2 × 12 V / 100 Ah lithium" },
  { k: "Range", o4: "37 km standard · 72 km max", o2: "37 km standard · 72 km max" },
  { k: "Top speed (AU)", o4: "10 km/h (Class 3 mobility limit)", o2: "10 km/h (Class 3 mobility limit)" },
  { k: "Max gradient", o4: "35° climb · 45° descent", o2: "30° climb", delta: { side: "o4", label: "+5° climb" } },
  { k: "Max side tilt", o4: "25°", o2: "20°", delta: { side: "o4", label: "+5°" } },
  { k: "Ground clearance", o4: "250 mm", o2: "180 mm", delta: { side: "o4", label: "+70 mm" } },
  { k: "Wheels / tyres", o4: "540 mm; smooth or knobbly off-road tread", o2: "540 mm; smooth or knobbly off-road tread" },
  { k: "Max user weight", o4: "190 kg", o2: "102 kg", delta: { side: "o4", label: "+88 kg" } },
  { k: "Length × Width × Height", o4: "1,630 × 850 × 930 mm", o2: "1,650 × 850 × 860 mm" },
  { k: "Kerb weight", o4: "From 145 kg (battery dependent)", o2: "From 145 kg (battery dependent)" },
  { k: "Turning circle", o4: "3.2 m", o2: "3.1 m", delta: { side: "o2", label: "0.1 m tighter" } },
  { k: "Electrical safety", o4: "Two independent circuits + overheat protection", o2: "Two independent circuits + overheat protection" },
  { k: "Classification", o4: "Class 3 medical mobility device", o2: "Class 3 medical mobility device" },
];

export default function ModelsComparison() {
  return (
    <section id="models" data-testid="models" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
            The Range
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight font-bold leading-[1.05]">
            Choose your <br /><span className="text-primary">Overlander.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            Two vehicles. Two very different missions. Both Class 3 legal in Australia and built
            to hand independence back to the rider.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {MODELS.map((m) => (
            <article
              key={m.id}
              data-testid={`model-${m.id}`}
              className={`relative flex flex-col rounded-sm border bg-card overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                m.booked ? "border-primary/40" : "border-border"
              }`}
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                <img
                  src={m.image}
                  alt={`${m.name} — ${m.tagline}`}
                  className={`absolute inset-0 h-full w-full object-cover ${m.booked ? "" : "opacity-90"}`}
                />
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-sm border ${m.statusColor}`}>
                    {m.booked ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                    {m.status}
                  </span>
                </div>
                {m.imageCredit && (
                  <div className="absolute bottom-3 right-3">
                    <span
                      data-testid={`model-image-credit-${m.id}`}
                      className="inline-block text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-sm bg-black/50 text-white backdrop-blur-sm"
                    >
                      {m.imageCredit}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-1 flex flex-col p-7 md:p-8">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 md:gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                      <m.icon className="h-3.5 w-3.5 text-primary" /> {m.audience}
                    </div>
                    <h3 className="mt-3 font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
                      {m.name}
                    </h3>
                    <p className="mt-2 text-primary font-semibold uppercase tracking-tight text-sm">
                      {m.tagline}
                    </p>
                  </div>
                  <div className="text-left md:text-right shrink-0 order-first md:order-none">
                    <div
                      data-testid={`model-price-${m.id}`}
                      className="font-display text-xl md:text-2xl font-black text-foreground whitespace-nowrap"
                    >
                      {m.price}
                    </div>
                    {m.priceNote && (
                      <div className="mt-1 text-[10px] uppercase tracking-[0.2em] font-bold text-primary">
                        {m.priceNote}
                      </div>
                    )}
                  </div>
                </div>

                <ul className="mt-6 space-y-2.5 text-sm text-foreground/85">
                  {m.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-8">
                  {m.id === "overlander-2" ? (
                    <Overlander2InterestDialog
                      triggerLabel={m.cta.label}
                      triggerTestId={`model-cta-${m.id}`}
                    />
                  ) : (
                    <Button
                      asChild
                      data-testid={`model-cta-${m.id}`}
                      variant={m.cta.primary ? "default" : "outline"}
                      className="w-full rounded-sm uppercase tracking-wider font-bold py-6 text-sm group"
                    >
                      <a href={m.cta.href}>
                        {m.cta.label}
                        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Side-by-side compare table */}
        <div className="mt-14 md:mt-20" data-testid="compare-table">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div className="max-w-xl">
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight font-bold leading-[1.05]">
                Every number, <br className="hidden sm:block" />side by side.
              </h3>
            </div>
          </div>

          <div className="mt-8 border border-border rounded-sm bg-card">
            {/* Sticky header — desktop (md+): 3-col matching the grid rows */}
            <div className="hidden md:grid grid-cols-[minmax(0,220px)_1fr_1fr] gap-x-6 px-6 py-4 bg-foreground text-background sticky top-16 z-20 rounded-t-sm">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold opacity-60">
                Spec
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold">
                Overlander 4
              </div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold">
                Overlander 2
              </div>
            </div>

            {/* Sticky header — mobile: compact two-model band so O4/O2 pills always resolve */}
            <div className="md:hidden grid grid-cols-2 px-4 py-2.5 bg-foreground text-background sticky top-16 z-20 rounded-t-sm">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border-r border-background/20 pr-3">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary" />
                Overlander 4
              </div>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold pl-3 opacity-80">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-background/50" />
                Overlander 2
              </div>
            </div>

            {COMPARE_ROWS.map((r, i) => (
              <div
                key={r.k}
                data-testid={`compare-row-${r.k.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`grid grid-cols-1 md:grid-cols-[minmax(0,220px)_1fr_1fr] gap-y-1 md:gap-x-6 px-6 py-4 ${
                  i % 2 === 0 ? "bg-background/60" : "bg-background"
                } border-t border-border`}
              >
                <div className="text-[10.5px] md:text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground md:self-center">
                  {r.k}
                </div>
                <div className="mt-1 md:mt-0 font-display text-sm md:text-[15px] font-semibold text-foreground leading-snug md:self-center">
                  <span className="md:hidden text-[10px] uppercase tracking-[0.2em] font-bold text-primary mr-2 align-middle">
                    O4
                  </span>
                  {r.o4}
                  {r.delta?.side === "o4" && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-700 text-[10px] uppercase tracking-[0.1em] font-bold align-middle">
                      <span aria-hidden="true">▲</span>
                      {r.delta.label}
                    </span>
                  )}
                </div>
                <div className="mt-1 md:mt-0 font-display text-sm md:text-[15px] font-semibold leading-snug md:self-center">
                  <span className="md:hidden text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mr-2 align-middle">
                    O2
                  </span>
                  {r.o2 ? (
                    <span className="text-foreground">{r.o2}</span>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                  {r.delta?.side === "o2" && (
                    <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-700 text-[10px] uppercase tracking-[0.1em] font-bold align-middle">
                      <span aria-hidden="true">▲</span>
                      {r.delta.label}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
